import { Avatar } from "@mui/material";
import { CiCalendar } from "react-icons/ci";
import { FiDollarSign } from "react-icons/fi";
import { GoDownload, GoPeople } from "react-icons/go";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from "recharts";
import useCommission from "./hooks/useCommission";
import { useEffect } from "react";
import React from "react";

const ByStructureDetails = () => {
  const navigate = useNavigate();
  const { data, method } = useCommission();
  const { commStructureDetailsList } = data;
  const { commissionStructureDetailsAPI, handleDownloadCommissionDetails } = method;
  const tiers = commStructureDetailsList?.tierMeta?.tiers || []

  const calculateTierProgress = (tiers, targetAchieved) => {
    const result = { name: "Progress" };

    tiers.forEach((tier) => {
      const range = tier.targetTo - tier.targetFrom;

      const filled = Math.min(
        Math.max(targetAchieved - tier.targetFrom, 0),
        range
      );

      result[`${tier.tierName}_filled`] = filled;
      result[`${tier.tierName}_remaining`] = range - filled;
    });

    return [result];
  };

  const maxTarget =
    commStructureDetailsList?.tierMeta?.tiers[
      commStructureDetailsList?.tierMeta?.tiers.length - 1
    ].targetTo;

  const colors = [
    "#00DE71",
    "#EC9384",
    "#6C9EFF",
    "#FFC107",
    "#9C27B0",
    "#00BCD4",
  ];

  const progressData = [
    {
      name: "Progress",
      tier1: commStructureDetailsList?.tierMeta?.tiers[0]?.targetFrom,
      tier2:
        commStructureDetailsList?.tierMeta?.tiers[
          commStructureDetailsList?.tierMeta?.tiers?.length - 1
        ]?.targetTo,
    },
  ];
  const { STRUCTURE_ID, groupId } = useParams();
  const CHART_MARGIN = { top: 0, right: 10, left: 10, bottom: 0 };
  const getTierWidthPercent = (tier, maxTarget) => {
    const range = tier.targetTo - tier.targetFrom;
    return (range / maxTarget) * 100;
  };

  useEffect(() => {
    if (STRUCTURE_ID) {
      commissionStructureDetailsAPI(STRUCTURE_ID, groupId);
    }
  }, [STRUCTURE_ID]);

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toISOString().split("T")[0];
  };
   const currencySymbol = "₹";

  return (
    <section>
      <ToastContainer />
      <div className="m-t-3 cus-padd cusheight">
        <header>
          <div className="d-flex justify-content-between">
            <div>
              <h2 className="page-tittle">
                <i
                  className="fa-solid fa-angle-left cusgap cursor"
                  onClick={() => navigate(-1)}
                />
                {commStructureDetailsList?.structure?.structureName}
              </h2>
              <span style={{ fontSize: "12px", paddingLeft: "18px" }}>
                {commStructureDetailsList?.structure?.modelType ===
                "TARGET_BASED"
                  ? "Target Based"
                  : "Item Based"}{" "}
                • {commStructureDetailsList?.structure?.groupName}
              </span>
            </div>

            <button 
              onClick={() =>
    handleDownloadCommissionDetails(STRUCTURE_ID, groupId)
  }
              className="btn px-4"
              style={{
                background: "linear-gradient(to right, #EA8F80, #F09A8B)",
                color: "#FFF",
                border: "none",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "500",
                paddingBlock: "10px",
              }}
            >
              <GoDownload fontSize={18} style={{ marginRight: "6px" }} />
              Download Report
            </button>
          </div>
          <hr />
        </header>
        <div>
          <div>
            {/* Top Summary Cards */}
            <div className="row g-3 mb-4 align-items-stretch">
              <div className="col-md-4">
                <div className="shadow-sm p-3 h-100 d-flex align-items-center rounded-3">
                  <div className="d-flex justify-content-between w-100">
                    <div>
                      <small className="text-muted">Total Commission</small>
                      <h4 className="mb-0">
                        {
                          commStructureDetailsList?.summaryCards
                            ?.totalCommission
                        }
                      </h4>
                    </div>
                    <FiDollarSign fontSize={18} color="#00DE71" />
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="shadow-sm p-3 h-100 d-flex align-items-center rounded-3">
                  <div className="d-flex justify-content-between w-100">
                    <div>
                      <small className="text-muted">Team Members</small>
                      <h4 className="mb-0">
                        {commStructureDetailsList?.summaryCards?.teamMembers}
                      </h4>
                    </div>
                    <GoPeople fontSize={18} color="#CC8DFF" />
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="shadow-sm p-3 h-100 d-flex align-items-center rounded-3">
                  <div className="d-flex justify-content-between w-100">
                    <div className="d-flex flex-column">
                      <small className="text-muted">Validity Period</small>
                      <small className="mb-0">
                        {formatDate(
                          commStructureDetailsList?.summaryCards?.startDate
                        )}{" "}
                        -{" "}
                        {formatDate(
                          commStructureDetailsList?.summaryCards?.endDate
                        )}
                      </small>
                    </div>
                    <CiCalendar fontSize={18} color="#EC9384" />
                  </div>
                </div>
              </div>
            </div>

            {/* Member Card */}
            {commStructureDetailsList?.members?.map((items, index) => (
              <div key={items?._id || index} className="shadow-sm p-4 mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center gap-3">
                    <Avatar
                      alt="Remy Sharp"
                      src="/static/images/avatar/1.jpg"
                    />
                    <h5 className="mb-0">{items?.staffName}</h5>
                  </div>

                  <div className="text-end">
                    <h5 className="mb-0"> {currencySymbol}{items?.totalEarned}</h5>
                    <small className="text-muted">Total Earned</small>
                  </div>
                </div>

                {/* Tier Progress */}
                <div className="mb-2">
                  <div
                    style={{
                      display: "flex",
                      width: "100%",
                      paddingLeft: CHART_MARGIN.left,
                      paddingRight: CHART_MARGIN.right,
                      boxSizing: "border-box",
                      marginBottom: 6,
                    }}
                  >
                    {tiers.map((tier, index) => (
                      <div
                        key={tier.tierName || index}
                        style={{
                          width: `${getTierWidthPercent(tier, maxTarget)}%`,
                          textAlign:
                            index === 0
                              ? "left"
                              : index === tiers.length - 1
                              ? "right"
                              : "center",
                          fontSize: "12px",
                          color: colors[index % colors.length],
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {tier.tierName}
                      </div>
                    ))}
                  </div>

                  <div style={{ width: "100%", height: 13 }}>
                    <ResponsiveContainer width="100%" height={18}>
                      <BarChart
                        data={calculateTierProgress(
                          tiers,
                          items?.targetAchieved
                        )}
                        layout="vertical"
                        margin={CHART_MARGIN}
                      >
                        <XAxis type="number" domain={[0, maxTarget]} hide />
                        <YAxis type="category" dataKey="name" hide />

                        {tiers.map((tier, index) => (
                          <React.Fragment key={tier.tierName || index}>
                            <Bar
                              dataKey={`${tier.tierName}_filled`}
                              stackId="progress"
                              fill={colors[index % colors.length]}
                              radius={index === 0 ? [6, 0, 0, 6] : [0, 0, 0, 0]}
                            />
                            <Bar
                              dataKey={`${tier.tierName}_remaining`}
                              stackId="progress"
                              fill="#E6E6E6"
                              radius={
                                index === tiers.length - 1
                                  ? [0, 6, 6, 0]
                                  : [0, 0, 0, 0]
                              }
                            />
                          </React.Fragment>
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      width: "100%",
                      paddingLeft: CHART_MARGIN.left,
                      paddingRight: CHART_MARGIN.right,
                      boxSizing: "border-box",
                      marginTop: 6,
                    }}
                  >
                    {tiers.map((tier, index) => {
                      const isLast = index === tiers.length - 1;

                      return (
                        <div
                          key={tier.tierName || index}
                          style={{
                            width: `${getTierWidthPercent(tier, maxTarget)}%`,
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: "11px",
                            color: "#666",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {/* Left boundary of this tier */}
                          <span>{tier.targetFrom}</span>

                          {/* Only the last segment prints the final max */}
                          {isLast ? <span>{maxTarget}</span> : <span />}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Table */}
                <div className="table-responsive mt-4">
                  <table className="table">
                    <thead className="table-light">
                      <tr>
                        <th>Date</th>
                        <th>Sales Amount</th>
                        <th>Commission Earned</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items?.rows?.map((row, i) => (
                        <tr key={row._id || i}>
                          <td style={{ textAlign: "left" }}>{row?.date}</td>
                          <td style={{ textAlign: "left" }}>
                            {row?.salesAmount}
                          </td>
                          <td
                            className="text-success"
                            style={{ textAlign: "left" }}
                          >
                            {row?.commissionEarned}
                          </td>
                        </tr>
                      ))}
                      <tr className="fw-bold">
                        <td
                          style={{ textAlign: "left" }}
                          className="table-light"
                        >
                          Total
                        </td>
                        <td
                          style={{ textAlign: "left" }}
                          className="table-light"
                        >
                          {items?.totalSales}
                        </td>
                        <td
                          className="text-success table-light"
                          style={{ textAlign: "left" }}
                        >
                          {items?.totalEarned}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ByStructureDetails;
